import { Refresh } from '@mui/icons-material'
import { Box, Tabs, Tab } from '@mui/material'
import { useMemo, useState } from 'react'
import { Statsig } from 'statsig-react'
import { useRouter } from 'next/router'
import { generatePath } from '@/lib/urlParse'
import { getCurrency } from '../../lib/currency'
import TrendTable from './components/trendTable'
import { BED_ROOMS_INFO_LIST } from './constData'

const RentByNeighborhood = ({ rentByNeighborhood = [], isPC, filterBy }) => {
  const route = useRouter()
  const { asPath } = route
  const { city, area } = filterBy || {}
  const [value, setValue] = useState(BED_ROOMS_INFO_LIST[0].key)
  const tabIdx = BED_ROOMS_INFO_LIST.findIndex(v => v.key === value)

  const options = useMemo(
    () => [
      {
        dataIndex: 'neighborhood',
        title: 'Neighborhood',
        render: ({ neighborhood, href }) => (
          <Box
            component="a"
            href={href}
            className="text-xs lg:text-base font-semibold cursor-pointer underline"
          >
            {neighborhood}
          </Box>
        )
      },
      {
        dataIndex: 'avgRent',
        title: 'Avg Rent',
        render: ({ avgRent: _avgRent }) => (
          <Box className="text-xs lg:text-base">
            ${getCurrency(Math.trunc(_avgRent)) || '--'}/{isPC ? 'month' : 'mo'}
          </Box>
        )
      },
      {
        dataIndex: 'avgSqft',
        title: isPC ? 'Average Sq.Ft' : 'Avg Sq.Ft',
        render: ({ avgSqft }) => (
          <Box className="text-xs lg:text-base">{getCurrency(avgSqft, 0) || '--'} Sq.Ft</Box>
        )
      },
      {
        dataIndex: 'annualChange',
        title: isPC ? 'Annual Change' : 'YoY',
        render: ({ annualChange }) => {
          // 非NaN：true
          const isValidValue = !Number.isNaN(parseFloat(annualChange)) && annualChange !== '0.0'
          return (
            <Box className="text-xs lg:text-base">
              {isValidValue && parseFloat(annualChange) > 0 ? '+' : ''}
              {isValidValue ? `${(parseFloat(annualChange) * 100).toFixed(1)}%` : '--'}
            </Box>
          )
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPC]
  )

  const onChangeTab = (_, newValue) => {
    setValue(newValue)
    const { floorPlanName = '' } = rentByNeighborhood?.[newValue] || {}

    if (process.env.STATSIG_API_KEY) {
      Statsig.logEvent('market_trends_neighborhood_apartment_type_click', asPath, {
        city,
        region: area,
        apartment_type: floorPlanName
      })
    }
  }

  const rentByNeighborhoodDetails = useMemo(() => {
    let result = []
    if (Array.isArray(rentByNeighborhood)) {
      const target = rentByNeighborhood.find(
        ({ floorPlanName }) => Number(floorPlanName) === tabIdx
      )
      if (target) {
        result = target?.rentByNeighborhoodDetails || []
      }
    }
    result = result.map(detail => {
      const { neighborhood } = detail
      const cityPath = generatePath(city, neighborhood)
      const bedRoomPath = BED_ROOMS_INFO_LIST[tabIdx].path
      return { ...detail, href: `${cityPath}/${bedRoomPath}` }
    })
    return result
  }, [rentByNeighborhood, tabIdx, city])

  return (
    <>
      <Box component="h2" className="mt-15 mb-6 lg:mt-20 font-ramaraja text-4xl">
        Rent by Neighborhood
      </Box>
      <Box className="flex my-6">
        <Refresh className="w-4 mr-2" />
        <div className="text-primary text-base">Last updated today</div>
      </Box>
      {rentByNeighborhood?.length ? (
        <>
          <Tabs
            sx={{
              minHeight: 'auto',
              '& .MuiTabs-indicator': {
                background: '#004D34'
              }
            }}
            variant="scrollable"
            scrollButtons={false}
            aria-label="scrollable auto tabs"
            value={value}
            onChange={onChangeTab}
          >
            {BED_ROOMS_INFO_LIST.map(({ label, key }) => (
              <Tab
                key={key}
                className={`capitalize mr-4 lg:mr-9 last:mr-0 text-lg font-semibold max-h-12 ${
                  key === value ? 'text-brand' : 'text-secondary'
                }`}
                label={label}
                disableRipple
                value={key}
                sx={{
                  '&.MuiTab-root': {
                    minWidth: 'auto',
                    padding: 0,
                    minHeight: 'auto',
                    maxHeight: 'auto',
                    height: '36px'
                  }
                }}
              />
            ))}
          </Tabs>
          <Box className="w-full mt-6">
            {rentByNeighborhoodDetails?.length ? (
              <TrendTable dataList={rentByNeighborhoodDetails} options={options} />
            ) : null}
          </Box>
        </>
      ) : null}
    </>
  )
}

export default RentByNeighborhood
